import React from 'react';

import { GridEventListener, GridRowParams } from '@mui/x-data-grid';
import { useGridApiRef } from '@mui/x-data-grid-pro';

import { reverse } from 'named-urls';

import { useLocation, useNavigate } from 'react-router-dom';

import Grid, { defaultFormatters } from 'components/Grid';

import { ColumnDefinition } from 'components/Grid/types';

import { AcknowledgementStatusOperator } from 'components/Grid/operators/Status';

import DueDate from 'components/DueDate';

import { DATETIME_COLUMN_MIN_WIDTH, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR, ProtectorType } from 'core/constants';

import routes from 'core/routes';

import { useStore } from 'utils/hooks/store';

import { formatMoment } from 'utils/formatters/timezone';

import { AcknowledgementRequestStatus, AcknowledgementRequestSummary } from 'utils/api/modules/entities/entities.types';

import RequestStatusChip from './RequestStatusChip';
import { NameCell, SecondaryCell, CompletionCell, CompletionProgress } from '../styles';

export const basicColumns = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 200,
    sortable: true,
    filterable: true,
    nativeFilter: 'name',
    protectorType: ProtectorType.ShortText,
    renderCell: ({ row }: { row: AcknowledgementRequestSummary }) => <NameCell>{row.name}</NameCell>,
  },
  {
    field: 'documentName',
    headerName: 'Document',
    flex: 1,
    minWidth: 180,
    sortable: true,
    filterable: true,
    nativeFilter: 'document_name',
    renderCell: ({ row }: { row: AcknowledgementRequestSummary }) => (
      <SecondaryCell>{row.document?.name ?? '—'}</SecondaryCell>
    ),
  },
  {
    field: 'owner',
    headerName: 'Owner',
    flex: 1,
    minWidth: 160,
    sortable: false,
    filterable: false,
    defaultVisibility: false,
    renderCell: ({ row }: { row: AcknowledgementRequestSummary }) => (
      <SecondaryCell>{row.owner?.name ?? '—'}</SecondaryCell>
    ),
  },
  {
    field: 'dueDate',
    headerName: 'Due date',
    width: 140,
    sortable: true,
    filterable: false,
    renderCell: ({ row }: { row: AcknowledgementRequestSummary }) => <DueDate date={row.dueDate} alwaysShowYear />,
    protectorType: ProtectorType.Date,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 140,
    sortable: false,
    filterable: true,
    nativeFilter: true,
    filterOperators: AcknowledgementStatusOperator(),
    renderCell: ({ row }: { row: AcknowledgementRequestSummary }) => (
      <RequestStatusChip status={row.status as AcknowledgementRequestStatus} />
    ),
  },
  {
    field: 'completionPercent',
    headerName: 'Completion',
    width: 160,
    sortable: false,
    filterable: false,
    renderCell: ({ row }: { row: AcknowledgementRequestSummary }) => (
      <CompletionCell>
        <CompletionProgress value={row.completionPercent} />
        {row.completionPercent}%
      </CompletionCell>
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    minWidth: DATETIME_COLUMN_MIN_WIDTH,
    sortable: true,
    filterable: false,
    defaultVisibility: false,
    renderCell: ({ row }: { row: AcknowledgementRequestSummary }) => (
      <SecondaryCell data-testid="acknowledgement-request-created-at">
        {formatMoment(row.createdAt, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}
      </SecondaryCell>
    ),
    valueFormatter: defaultFormatters.date_time,
    protectorType: ProtectorType.DateTime,
  },
] as unknown as ColumnDefinition[];

interface AcknowledgementRequestsGridProps {
  onRefreshCallback?: (callback: () => void) => void;
}

const URL_PARAM_TO_COLUMN: Record<string, { field: string; operator: string }> = {
  document_name: { field: 'documentName', operator: 'contains' },
  status: { field: 'status', operator: 'is' },
};

const AcknowledgementRequestsGrid: React.FunctionComponent<AcknowledgementRequestsGridProps> = ({
  onRefreshCallback,
}) => {
  const { routing } = useStore();
  const gridApiRef = useGridApiRef();
  const location = useLocation();
  const navigate = useNavigate();

  // Treat the URL filter params as a transient hand-off: lift them into Grid's filter model
  // (Grid's own localStorage then carries the visible/removable chip) and strip them from the
  // URL so a refresh doesn't silently re-apply. Grid loads saved preferences from the backend
  // asynchronously after mount, so we re-apply once that race has settled — otherwise a stale
  // saved filter would silently overwrite the URL-driven one.
  React.useEffect(() => {
    if (!gridApiRef.current) return;

    const params = new URLSearchParams(location.search);
    const items = Object.entries(URL_PARAM_TO_COLUMN)
      .map(([param, { field, operator }]) => {
        const value = params.get(param);
        return value ? { id: field, field, operator, value } : null;
      })
      .filter((item): item is { id: string; field: string; operator: string; value: string } => item !== null);

    if (items.length === 0) return;

    const apply = (): void => {
      gridApiRef.current?.setFilterModel({ items });
    };
    apply();
    const reapplyTimer = setTimeout(apply, 1500);
    navigate(location.pathname, { replace: true });
    return () => clearTimeout(reapplyTimer);
  }, [gridApiRef, location.pathname, location.search, navigate]);

  const onRowClick: GridEventListener<'rowClick'> = (item: GridRowParams): void => {
    routing.push(reverse(routes.dashboard.documents.acknowledgementRequests.details, { requestId: item.id as string }));
  };

  return (
    <Grid
      basicColumns={basicColumns}
      dataURL="/api/entities/documents/acknowledgement-requests/"
      sortBy={{ field: 'createdAt', sort: 'desc' }}
      onRowClick={onRowClick}
      onRefreshCallback={onRefreshCallback}
      customNoRowsText="No acknowledgement requests yet"
      disableSelectAllColumns
      disableExport
      showQuickFilter={false}
      gridApiRef={gridApiRef}
    />
  );
};

export default AcknowledgementRequestsGrid;
